import classNames from 'classnames';
import React, { ChangeEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MappingStatus } from '~/Models/DTO/Location.models';
import { Button, Link as MuiLink, TextField, Typography } from '~/UIKit';
import { AccessList, useHasAccess, useIntegrations } from '~/Hooks';
import { EventPageFloatingPanelStyles } from './EventPageFloatingPanel.styles';
import { useEventPageFloatingPanel } from './useEventPageFloatingPanel';
import { useLivestreamUpdate } from './useLivestreamUpdate';
import { ExternalPartyID } from '~/Models/DTO/externalParties.model';
import { Camera } from '~/Components/Icons';

export const EventPageFloatingPanel = () => {
  const { hasLiveStream, eventDate, mappingStatus } = useEventPageFloatingPanel();

  const { hasAccess } = useHasAccess();

  const { hasIntegration } = useIntegrations();
  const isUSCIntegrationEnabled = useMemo(() => hasIntegration(ExternalPartyID.USC_INTEGRATION), [
    hasIntegration,
  ]);

  const {
    livestreamLink,
    setLivestreamLink,
    shouldShowLink,
    setShouldShowLink,
    isLivestreamLinkValid,
  } = useLivestreamUpdate();

  const { t } = useTranslation();

  const updateLivestreamLink = (e: ChangeEvent<HTMLInputElement>) =>
    setLivestreamLink(e.target.value);
  const { classes } = EventPageFloatingPanelStyles()();

  return (
    <>
      <div
        className={classes.EventPageFloatingInfoPanelContainer}
        data-testid="EventPageFloatingPanel"
      >
        <div className={classes.EventPageFloatingInfoPanel}>
          <div className={classes.EventPageFloatingPanelItem}>
            <Typography>{t('app.import.table.date')}</Typography>
            <Button variant="text" className={classes.EventPageFloatingPanelButtons} disabled>
              {eventDate}
            </Button>
          </div>
          {isUSCIntegrationEnabled && hasAccess(AccessList.USC_SYNC_STATUS) && (
            <div className={classes.EventPageFloatingPanelItem}>
              <Typography>{t('integrations.usc.title')}</Typography>
              <Button variant="text" className={classes.EventPageFloatingPanelButtons} disabled>
                {/* Below the below InProcess part will be removed with CS-267 */}
                {(MappingStatus.InProcess === mappingStatus ||
                  MappingStatus.UnderReview === mappingStatus) &&
                  t('integration.processing')}
                {MappingStatus.Mapped === mappingStatus && t('integration.connected')}
              </Button>
            </div>
          )}
        </div>
      </div>
      {hasLiveStream && (
        <div className={classNames(classes.LiveStreamContainer)}>
          <div className={classes.TitleContainer}>
            <div className={classes.castIcon}>
              <Camera viewBox="0 0 24 24" />
            </div>
            <Typography className={classes.EventPageFloatingPanelTitle}>
              {t('app.livestream')}
            </Typography>
          </div>
          {isLivestreamLinkValid && shouldShowLink ? (
            <MuiLink
              className={classes.EventPageFloatingPanelLink}
              color="primary"
              underline="always"
              href={livestreamLink}
              rel="noopener"
              target="_blank"
            >
              {livestreamLink}
            </MuiLink>
          ) : (
            <TextField
              onBlur={setShouldShowLink}
              onFocus={setShouldShowLink}
              defaultValue={livestreamLink}
              onChange={updateLivestreamLink}
              error={!!livestreamLink && !isLivestreamLinkValid}
              placeholder={t('event.livestream.addLink')}
              className={classes.EventPageFloatingPanelInput}
              newDesign
            />
          )}
        </div>
      )}
    </>
  );
};
